import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, BASE } from '../api/client';
import type { Asset, AuthUser, Chat, Client, Instance, InstanceKind, Message, Project } from '../api/types';

// ----- Auth -----
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<AuthUser>('/auth/me'),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => qc.clear(),
  });
}

// ----- Projects -----
export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<Project[]>('/projects'),
  });
}

export function useProject(slug: string | undefined) {
  return useQuery({
    queryKey: ['project', slug],
    queryFn: () => api.get<Project>(`/projects/${slug}`),
    enabled: !!slug,
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; clientId?: string; description?: string }) =>
      api.post<Project>('/projects', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['clients'] });
    },
  });
}

// ----- Clients -----
export function useClients() {
  return useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<Client[]>('/clients'),
    staleTime: 60_000,
  });
}

export function useClient(id: string | undefined) {
  return useQuery({
    queryKey: ['client', id],
    queryFn: () =>
      api.get<Client & { projects: Array<{ id: string; slug: string; name: string; description: string | null; createdAt: string }> }>(
        `/clients/${id}`,
      ),
    enabled: !!id,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; contactEmail?: string; notes?: string }) =>
      api.post<Client>('/clients', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useUpdateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; contactEmail?: string | null; notes?: string | null }) =>
      api.patch<Client>(`/clients/${id}`, input),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client', variables.id] });
    },
  });
}

export function useArchiveClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<Client>(`/clients/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

// ----- Tree (sidebar) -----
export interface TreeSessionNode {
  id: string;
  title: string | null;
  agentSlug: string;
}
export interface TreeAssetNode {
  id: string;
  slug: string;
  name: string;
  kind: string;
  /** Sub-tipo canónico (`character.human`, `environment.2d-empty`, etc.). El sidebar agrupa por sección a partir de esto. NULL en instances viejas. */
  subtype: string | null;
  status: string;
  sessions: TreeSessionNode[];
}
export interface TreeProjectNode {
  id: string;
  slug: string;
  name: string;
  archived: boolean;
  assets: TreeAssetNode[];
}
export interface TreeClientNode {
  client: string;
  projects: TreeProjectNode[];
}

export function useTree() {
  return useQuery({
    queryKey: ['tree'],
    queryFn: () => api.get<TreeClientNode[]>('/projects/tree'),
    staleTime: 30_000,
  });
}

// ----- Instances -----
export function useInstances(projectSlug: string | undefined) {
  return useQuery({
    queryKey: ['instances', projectSlug],
    queryFn: () => api.get<Instance[]>(`/projects/${projectSlug}/instances`),
    enabled: !!projectSlug,
  });
}

export function useCreateInstance(projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; kind: InstanceKind; subtype?: string }) =>
      api.post<Instance>(`/projects/${projectSlug}/instances`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', projectSlug] });
      // Sin esto el sidebar (árbol) no se actualiza → hay que recargar la página.
      qc.invalidateQueries({ queryKey: ['tree'] });
    },
  });
}

// ----- Asset types catalog -----
export function useAssetTypesCatalog() {
  return useQuery({
    queryKey: ['asset-types-catalog'],
    queryFn: () => api.get<import('../api/types').AssetTypesCatalog>('/asset-types'),
    staleTime: 60 * 60 * 1000, // 1h — catálogo estático entre deploys
  });
}

// ----- Keyframe-sequence (video largo por keyframes) -----
export interface KeyframeSequenceJobStatus {
  id: string;
  status: 'pending' | 'generating' | 'concatenating' | 'completed' | 'failed';
  keyframesCount: number;
  clipsGenerated: number;
  clipsExpected: number;
  finalVideoAssetId: string | null;
  errorMessage: string | null;
  costUsd: number | null;
  startedAt: string | null;
  completedAt: string | null;
}

export function useEnqueueKeyframeSequence() {
  return useMutation({
    mutationFn: (input: {
      projectId?: string;
      instanceId?: string;
      keyframeAssetIds: string[];
      movementPrompts?: string[];
      model?: string;
      durationPerClip?: number;
      aspectRatio?: string;
    }) =>
      api.post<{ jobId: string; status: string }>('/keyframe-sequence/jobs', input),
  });
}

export function useKeyframeSequenceJobStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['keyframe-sequence-job', jobId],
    queryFn: () => api.get<KeyframeSequenceJobStatus>(`/keyframe-sequence/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const data = q.state.data as KeyframeSequenceJobStatus | undefined;
      if (!data) return 5000;
      if (data.status === 'completed' || data.status === 'failed') return false;
      return 5000;
    },
  });
}

export function useUpdateInstanceBrief(projectSlug: string, instanceSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (briefText: string) =>
      api.patch<Instance>(`/projects/${projectSlug}/instances/${instanceSlug}`, { briefText }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['instances', projectSlug] });
      qc.invalidateQueries({ queryKey: ['tree'] });
    },
  });
}

// ----- Chats -----
export function useChat(chatId: string | undefined) {
  return useQuery({
    queryKey: ['chat', chatId],
    queryFn: () => api.get<Chat>(`/chats/${chatId}`),
    enabled: !!chatId,
  });
}

export function useChatMessages(chatId: string | undefined) {
  return useQuery({
    queryKey: ['chat', chatId, 'messages'],
    queryFn: () => api.get<Message[]>(`/chats/${chatId}/messages`),
    enabled: !!chatId,
  });
}

/**
 * 1:1 con instance. Devuelve el chat de la instance, creándolo si no existe.
 * Reemplaza al viejo useCreateChat + listByInstance.
 */
export function useChatForInstance(instanceId: string | null | undefined, agentSlug?: string) {
  return useQuery({
    queryKey: ['chat-for-instance', instanceId, agentSlug],
    queryFn: () =>
      api.get<Chat>(
        `/chats/for-instance/${instanceId}${agentSlug ? `?agentSlug=${agentSlug}` : ''}`,
      ),
    enabled: !!instanceId,
  });
}

export function useUpdateChat(chatId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title?: string | null;
      modelOverride?: string | null;
      falModelOverride?: string | null;
    }) => api.patch<Chat>(`/chats/${chatId}`, input),
    onSuccess: (data) => qc.setQueryData(['chat', chatId], data),
  });
}

// ----- Assets -----
export function useChatAssets(chatId: string | undefined) {
  return useQuery({
    queryKey: ['chat', chatId, 'assets'],
    queryFn: () => api.get<Asset[]>(`/chats/${chatId}/assets`),
    enabled: !!chatId,
    staleTime: 5_000,
  });
}

/**
 * Lista assets de un proyecto.
 *  - Sin `instanceId`: TODAS las refs del proyecto (modal "📚 Biblioteca").
 *  - Con `instanceId`: SOLO las refs subidas para ese asset/instancia. Es lo
 *    que vemos en el ReferenceTray del asset — cada asset arranca con sus
 *    propias refs, no se mezclan con las de hermanos del mismo proyecto.
 */
export function useProjectAssets(projectSlug: string | undefined, instanceId?: string) {
  return useQuery({
    queryKey: ['project', projectSlug, 'assets', instanceId ?? 'all'],
    queryFn: () => {
      const qs = instanceId ? `?instanceId=${instanceId}` : '';
      return api.get<Asset[]>(`/projects/${projectSlug}/assets${qs}`);
    },
    enabled: !!projectSlug,
    staleTime: 5_000,
  });
}

export function useDeleteProjectAsset(projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => api.del<void>(`/assets/${assetId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', projectSlug, 'assets'] }),
  });
}

export interface LibraryAsset extends Asset {
  projectSlug: string;
  projectName: string;
  projectClient: string | null;
}

export function useAssetLibrary(filter: { q?: string; client?: string; projectId?: string }) {
  const qs = new URLSearchParams();
  if (filter.q) qs.set('q', filter.q);
  if (filter.client) qs.set('client', filter.client);
  if (filter.projectId) qs.set('projectId', filter.projectId);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery({
    queryKey: ['asset-library', filter.q ?? '', filter.client ?? '', filter.projectId ?? ''],
    queryFn: () => api.get<LibraryAsset[]>(`/assets/library${suffix}`),
    staleTime: 10_000,
  });
}

export function useImportAsset(projectSlug: string, instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) =>
      api.post<Asset>(`/assets/${assetId}/import`, { instanceId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectSlug, 'assets', instanceId] });
    },
  });
}

/** Versión que refresca el panel lateral del chat. */
export function useImportAssetForChat(chatId: string, instanceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) =>
      api.post<Asset>(`/assets/${assetId}/import`, { instanceId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', chatId, 'assets'] });
    },
  });
}

export function useUpdateAssetDescription(projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, description }: { assetId: string; description: string }) =>
      api.patch<Asset>(`/assets/${assetId}`, { description }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project', projectSlug, 'assets'] }),
  });
}

/**
 * Igual que useUpdateAssetDescription pero invalida la query del chat
 * (ReferenceTray dentro del ChatPanel). Útil cuando no tenemos projectSlug
 * a mano (el ReferenceTray solo conoce chatId).
 */
export function useUpdateAssetDescriptionForChat(chatId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, description }: { assetId: string; description: string }) =>
      api.patch<Asset>(`/assets/${assetId}`, { description }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat', chatId, 'assets'] }),
  });
}

/** Payload de calificación: estrellas y/o motivo del operador (chips + comentario).
 * Todo opcional salvo assetId — se puede mandar solo estrellas, solo motivo, o ambos. */
export interface RateAssetInput {
  assetId: string;
  userRating?: number;
  userRatingReason?: string;
  userRatingTags?: string[];
}

export function useRateAsset(projectSlug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, ...body }: RateAssetInput) =>
      api.patch<Asset>(`/assets/${assetId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectSlug, 'assets'] });
      qc.invalidateQueries({ queryKey: ['instance-fal-calls'] });
    },
  });
}

/** Versión scoped al chat: invalida los assets del panel lateral del chat
 * para que el rating se refleje al instante. */
export function useRateAssetForChat(chatId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ assetId, ...body }: RateAssetInput) =>
      api.patch<Asset>(`/assets/${assetId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', chatId, 'assets'] });
      qc.invalidateQueries({ queryKey: ['instance-fal-calls'] });
    },
  });
}

export interface FalCallSummary {
  id: string;
  chatId: string;
  generationJobId: string | null;
  endpoint: string;
  promptText: string | null;
  payload: unknown;
  costUsd: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
  durationMs: number | null;
  generatedAssets: Array<{
    id: string;
    mimeType: string;
    width: number | null;
    height: number | null;
    shortRef: string | null;
    description: string | null;
    userRating: number | null;
    aiRating: number | null;
    aiRatingReason: string | null;
  }>;
}

export function useInstanceFalCalls(projectSlug: string | undefined, instanceSlug: string | undefined) {
  return useQuery({
    queryKey: ['instance-fal-calls', projectSlug, instanceSlug],
    queryFn: () =>
      api.get<FalCallSummary[]>(`/projects/${projectSlug}/instances/${instanceSlug}/fal-calls`),
    enabled: !!projectSlug && !!instanceSlug,
    staleTime: 5_000,
  });
}

export interface ChatCostTotal {
  llm: number;
  fal: number;
  total: number;
}

export function useChatCostTotal(chatId: string | undefined) {
  return useQuery({
    queryKey: ['chat-cost-total', chatId],
    queryFn: () => api.get<ChatCostTotal>(`/chats/${chatId}/cost-total`),
    enabled: !!chatId,
    staleTime: 5_000,
  });
}

export interface ChatLlmDetailRow {
  id: string;
  model: string;
  agentSlug: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costUsd: number;
  durationMs: number | null;
  errorMessage: string | null;
  createdAt: string;
}

export function useChatCostDetail(chatId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['chat-cost-detail', chatId],
    queryFn: () => api.get<ChatLlmDetailRow[]>(`/chats/${chatId}/cost-detail`),
    enabled: !!chatId && enabled,
    staleTime: 5_000,
  });
}

export interface ActiveJob {
  id: string;
  status: 'queued' | 'submitted' | 'in_progress';
  endpoint: string;
  costEstimateUsd: number | null;
  createdAt: string;
}

export function useChatActiveJobs(chatId: string | undefined) {
  return useQuery({
    queryKey: ['chat-active-jobs', chatId],
    queryFn: () => api.get<ActiveJob[]>(`/chats/${chatId}/active-jobs`),
    enabled: !!chatId,
    // Polling: cada 8s mientras la pestaña esté visible. El SSE
    // job.completed/failed también dispara invalidate para corte rápido.
    refetchInterval: 8_000,
    refetchIntervalInBackground: false,
    staleTime: 4_000,
  });
}

export function useRetryJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, jobId }: { chatId: string; jobId: string }) =>
      api.post<{ job_id: string; status: string }>(`/chats/${chatId}/jobs/${jobId}/retry`),
    onSuccess: (_, { chatId }) => {
      qc.invalidateQueries({ queryKey: ['chat-active-jobs', chatId] });
      qc.invalidateQueries({ queryKey: ['instance-fal-calls'] });
    },
  });
}

export interface FailedBatch {
  messageId: string;
  jobs: Array<{
    id: string;
    endpoint: string;
    errorMessage: string | null;
    costEstimateUsd: number | null;
    createdAt: string;
  }>;
}

export function useChatLastFailedBatch(chatId: string | undefined) {
  return useQuery({
    queryKey: ['chat-failed-batch', chatId],
    queryFn: () => api.get<FailedBatch | null>(`/chats/${chatId}/last-failed-batch`),
    enabled: !!chatId,
    staleTime: 5_000,
  });
}

export type RetryFailedBatchResponse =
  | { mode: 'requeued'; retried: number; jobIds: string[] }
  | { mode: 'needs-llm'; userMessageId: string };

export function useRetryFailedBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ chatId, messageId }: { chatId: string; messageId: string }) =>
      api.post<RetryFailedBatchResponse>(`/chats/${chatId}/retry-failed-batch`, {
        messageId,
      }),
    onSuccess: (_, { chatId }) => {
      qc.invalidateQueries({ queryKey: ['chat-active-jobs', chatId] });
      qc.invalidateQueries({ queryKey: ['chat-failed-batch', chatId] });
      qc.invalidateQueries({ queryKey: ['instance-fal-calls'] });
    },
  });
}

export function useInvalidateChatAssets() {
  const qc = useQueryClient();
  return (chatId: string) => qc.invalidateQueries({ queryKey: ['chat', chatId, 'assets'] });
}

export function useDeleteAsset(chatId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assetId: string) => api.del<void>(`/assets/${assetId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat', chatId, 'assets'] }),
  });
}

// ----- Agents -----
export interface AgentSummary {
  slug: string;
  name: string;
  description: string;
  /** Modelo efectivo (override global si hay, sino el del .md). */
  model: string;
  /** Modelo del .md (default del sistema). */
  defaultModel: string;
  /** True si hay override global seteado desde la UI. */
  overridden: boolean;
  category: 'xvision' | 'fal' | 'other';
}

export interface ModelChoice {
  alias: string;
  id: string;
  label: string;
}

export function useAgents() {
  return useQuery({
    queryKey: ['agents'],
    queryFn: () => api.get<AgentSummary[]>('/agents'),
    staleTime: 10 * 60 * 1000, // los agentes cambian poco — cache 10 min
  });
}

/** Modelos elegibles desde la UI (sonnet/opus/haiku). */
export function useModelChoices() {
  return useQuery({
    queryKey: ['model-choices'],
    queryFn: () => api.get<ModelChoice[]>('/agents/model-choices'),
    staleTime: 60 * 60 * 1000,
  });
}

export interface FalModelOption {
  endpoint: string;
  label: string;
  forWhat: string;
  costHint: string;
  isDefault?: boolean;
}

/** Catálogo curado de modelos de generación de fal.ai para una sección (agentSlug). */
export function useFalModelOptions(agentSlug: string | undefined) {
  return useQuery({
    queryKey: ['fal-model-options', agentSlug],
    queryFn: () => api.get<FalModelOption[]>(`/agents/fal-model-options/${agentSlug}`),
    enabled: !!agentSlug,
    staleTime: 60 * 60 * 1000,
  });
}

/** Setea (model) o limpia (null) el override global de modelo de un agente. */
export function useSetAgentModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, model }: { slug: string; model: string | null }) =>
      model === null
        ? api.del<AgentSummary>(`/agents/${slug}/model`)
        : api.patch<AgentSummary>(`/agents/${slug}/model`, { model }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['agents'] }),
  });
}

// ----- Costs (tracking dual global del usuario) -----
export interface CostSummary {
  llm: { totalUsd: number };
  fal: { totalUsd: number };
  elevenlabs: {
    charsTracked: number;
    lastSnapshot: {
      characterCountAfter: number;
      characterLimitAtTime: number;
      percentUsedAfter: number;
      nextResetUnix: number | null;
      tier: string | null;
    } | null;
    live: {
      characterCount: number;
      characterLimit: number;
      percentUsed: number;
      nextResetUnix: number;
      nextResetIso: string;
      tier: string;
    } | null;
    liveError: string | null;
  };
  totalUsd: number;
}

export function useCostSummary() {
  return useQuery({
    queryKey: ['costs', 'summary'],
    queryFn: () => api.get<CostSummary>('/costs/summary'),
    refetchInterval: 60 * 1000, // refresh cada 1 min
    staleTime: 30 * 1000,
  });
}

// ----- Character Creator -----
export interface CharacterEntry {
  id: string;
  projectId: string | null;
  assetId: string;
  voiceId: string | null;
  voiceProvider: string | null;
  name: string;
  description: string | null;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
  asset?: Asset;
}

/**
 * Galería de personajes:
 *  - `projectId === undefined` → catálogo global (proyecto null en backend). Default.
 *  - `projectId === 'all'` → globales + de cualquier proyecto.
 *  - `projectId === '<uuid>'` → solo de ese proyecto.
 */
export function useCharacters(projectId: string | undefined, includeArchived = false) {
  return useQuery({
    queryKey: ['characters', projectId ?? 'global', includeArchived],
    queryFn: () => {
      const params = new URLSearchParams();
      if (projectId !== undefined) params.set('projectId', projectId);
      if (includeArchived) params.set('includeArchived', 'true');
      const qs = params.toString();
      return api.get<CharacterEntry[]>(`/character-creator${qs ? `?${qs}` : ''}`);
    },
  });
}

export function useCreateCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      projectId?: string | null;
      assetId: string;
      name: string;
      voiceId?: string;
      voiceProvider?: string;
      description?: string;
    }) => api.post<CharacterEntry>('/character-creator', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['characters'] });
    },
  });
}

export function useUpdateCharacter(_projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: {
        name?: string;
        voiceId?: string | null;
        voiceProvider?: string | null;
        description?: string | null;
        archived?: boolean;
        projectId?: string | null;
      };
    }) => api.patch<CharacterEntry>(`/character-creator/${id}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['characters'] }),
  });
}

export function useArchiveCharacter(_projectId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<CharacterEntry>(`/character-creator/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['characters'] }),
  });
}

// ----- Voices (ElevenLabs Pool A) -----
export interface ElevenLabsVoice {
  voice_id: string;
  name: string;
  category?: string;
  description?: string;
  preview_url?: string;
  isCloned: boolean;
}

export function useElevenlabsVoices() {
  return useQuery({
    queryKey: ['voices', 'elevenlabs'],
    queryFn: () => api.get<ElevenLabsVoice[]>('/voices/elevenlabs'),
    staleTime: 5 * 60 * 1000,
  });
}

// ----- Voice Cost Advisor (task #61) -----
export interface VoiceAdvisorStatus {
  tier: string;
  characterCount: number;
  characterLimit: number;
  percentUsed: number;
  alertLevel: 'ok' | 'warning' | 'overage' | 'critical';
  nextResetUnix: number;
  nextResetIso: string;
  message: string | null;
  upgradeRecommended: boolean;
  upgradeReason: string | null;
}

export interface VoiceCostComparison {
  poolA: {
    pool: 'A';
    provider: string;
    marginalCostUsd: number;
    charsConsumed: number;
    percentOfPlan: number;
    withinPlan: boolean;
  };
  poolB: Array<{
    pool: 'B';
    provider: string;
    estimatedCostUsd: number;
    costPer1kChars: number;
    notes?: string;
  }>;
}

export function useVoiceAdvisorStatus() {
  return useQuery({
    queryKey: ['voices', 'cost-advisor'],
    queryFn: () => api.get<VoiceAdvisorStatus | null>('/voices/cost-advisor'),
    refetchInterval: 60 * 1000,
    staleTime: 30 * 1000,
  });
}

export function useVoiceCostCompare(chars: number, enabled: boolean = true) {
  return useQuery({
    queryKey: ['voices', 'cost-compare', chars],
    queryFn: () => api.get<VoiceCostComparison>(`/voices/cost-compare?chars=${chars}`),
    enabled: enabled && chars > 0,
    staleTime: 60 * 1000,
  });
}

// ----- Character Creator catalog (lipsync models + emotions) -----
export interface LipsyncModelOption {
  key: string;
  label: string;
  pricing: { type: 'per-second' | 'flat'; usd: number };
  preferredImage: 'closeup' | 'medium';
  estimatedJobSeconds: { min: number; max: number };
  notes: string;
}

export interface EmotionOption {
  key: string;
  label: string;
  emoji: string;
}

export interface CharacterCreatorCatalog {
  lipsyncModels: LipsyncModelOption[];
  emotions: EmotionOption[];
}

export function useCharacterCreatorCatalog() {
  return useQuery({
    queryKey: ['character-creator', 'catalog'],
    queryFn: () => api.get<CharacterCreatorCatalog>('/character-creator/catalog'),
    staleTime: 60 * 60 * 1000, // 1h: el catálogo no cambia entre deploys
  });
}

/**
 * Devuelve (idempotente) el chatId del wizard conversacional para el user actual.
 * Crea la shell-instance + chat con agentSlug='xvision-personaje' la primera vez,
 * luego siempre devuelve el mismo (persistente entre sesiones).
 */
export function useCharacterCreatorChatSession() {
  return useMutation({
    mutationFn: () => api.post<{ chatId: string }>('/character-creator/chat-session', {}),
  });
}

/**
 * Abre (o reusa) un chat suelto con un agente del catálogo `/agents`. Cada par
 * (user, agentSlug) tiene su propia shell-instance en el proyecto interno
 * `__sandbox-chats` — los assets generados ahí siguen pesando contra el user
 * y aparecen en sus galerías globales (/backgrounds, /character-creator, etc.).
 */
export function useAgentChatSession() {
  return useMutation({
    mutationFn: (agentSlug: string) =>
      api.post<{ chatId: string }>(`/agents/${encodeURIComponent(agentSlug)}/chat-session`, {}),
  });
}

// ----- ElevenLabs admin (consumo total del plan compartido) -----

export interface ElevenlabsAccountSnapshot {
  plan: {
    tier: string;
    characterLimit: number;
    characterCount: number;
    percentUsed: number;
    nextResetUnix: number;
    nextResetIso: string;
  };
  breakdown: {
    xvision: { chars: number; percent: number };
    external: { chars: number; percent: number };
  };
}

export function useElevenlabsAccount() {
  return useQuery({
    queryKey: ['admin', 'elevenlabs', 'account'],
    queryFn: () => api.get<ElevenlabsAccountSnapshot>('/admin/elevenlabs/account'),
    staleTime: 60_000, // 1 min — el plan no cambia tan rápido
    refetchOnWindowFocus: false,
  });
}

// ----- Backgrounds gallery -----

export interface BackgroundAsset {
  id: string;
  bucketUrl: string | null;
  bucketKey: string | null;
  falCdnUrl: string | null;
  thumbnailKey: string | null;
  mimeType: string;
  width: number | null;
  height: number | null;
  sizeBytes: string | number | null;
  createdAt: string;
  metadata: { description?: string; shortRef?: string } | null;
  projectSlug: string;
  projectName: string;
  projectClient: string | null;
  bestUrl?: string | null;
}

export function useBackgrounds(filter: { q?: string; client?: string; projectId?: string } = {}) {
  const params = new URLSearchParams();
  if (filter.q) params.set('q', filter.q);
  if (filter.client) params.set('client', filter.client);
  if (filter.projectId) params.set('projectId', filter.projectId);
  const qs = params.toString();
  return useQuery({
    queryKey: ['backgrounds', filter],
    queryFn: () => api.get<BackgroundAsset[]>(`/backgrounds${qs ? `?${qs}` : ''}`),
  });
}

export function useUploadBackground() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BASE}/backgrounds/upload`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Upload falló (HTTP ${res.status}): ${text.slice(0, 200)}`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backgrounds'] }),
  });
}

// ----- Character Common Lines (task #62) -----
export interface CharacterCommonLine {
  id: string;
  characterId: string;
  text: string;
  emotion: string | null;
  lipsyncModel: string | null;
  imageMode: string;
  audioAssetId: string | null;
  lipsyncAssetId: string | null;
  createdAt: string;
  status: 'ready' | 'audio-only' | 'pending';
}

export function useCharacterLines(characterId: string | undefined) {
  return useQuery({
    queryKey: ['character-lines', characterId],
    queryFn: () => api.get<CharacterCommonLine[]>(`/character-creator/${characterId}/lines`),
    enabled: !!characterId,
  });
}

export function useCreateCharacterLine(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      text: string;
      emotion?: string;
      lipsyncModel?: string;
      imageMode?: 'medium' | 'closeup';
    }) => api.post<CharacterCommonLine>(`/character-creator/${characterId}/lines`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['character-lines', characterId] }),
  });
}

export function useDeleteCharacterLine(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) =>
      api.del<void>(`/character-creator/${characterId}/lines/${lineId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['character-lines', characterId] }),
  });
}

export interface PlayLineResult {
  lineId: string;
  jobId: string;
  status: 'pending' | 'completed' | 'failed';
  audioAssetId?: string;
  videoAssetId?: string;
  cached?: { audio: boolean; video: boolean };
}

export interface JobStatus {
  status: 'pending' | 'completed' | 'failed';
  audioAssetId?: string;
  videoAssetId?: string;
  errorMessage?: string;
  falRequestId?: string;
}

export function usePlayCharacterLine(characterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      lineId: string;
      mode?: 'video' | 'audio';
      voiceOverride?: string;
    }) =>
      api.post<PlayLineResult>(
        `/character-creator/${characterId}/lines/${input.lineId}/play`,
        { mode: input.mode, voiceOverride: input.voiceOverride },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['character-lines', characterId] }),
  });
}

// ----- Lipsync composite (avatar chico + fondo grande) -----
export interface LipsyncCompositeResult {
  compositeAssetId: string;
  lipsyncAssetId: string;
  audioAssetId: string;
  cached: { lipsync: boolean; composite: boolean };
}

// ----- Generador de personajes (wizard) -----
export interface GenerateCharacterInput {
  name: string;
  mode: 'with-ref' | 'from-scratch';
  type: 'human' | 'nonhuman';
  referenceAssetId?: string;
  logoAssetId?: string;
  attributes: {
    age?: string;
    gender?: string;
    profession?: string;
    nationality?: string;
    skinTone?: string;
    hair?: string;
    build?: string;
    clothing?: string;
    logoHandling?: 'KEEP' | 'REMOVE' | 'USE PROVIDED LOGO' | 'NONE';
    nametag?: string;
    props?: string;
  };
  style?: string;
  description?: string;
  /** Prompt editado a mano en el live-preview. Si viene, se usa tal cual. */
  promptOverride?: string;
}

export function useGenerateCharacter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateCharacterInput) =>
      api.post<{ characterId: string; assetId: string; cost: number }>(
        '/character-creator/generate',
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['characters'] }),
  });
}

/**
 * Live-preview del prompt del wizard: devuelve el texto EXACTO que se mandaría a
 * nano-banana con estos atributos, SIN generar nada (costo $0).
 */
export function usePreviewCharacterPrompt() {
  return useMutation({
    mutationFn: (
      input: Pick<GenerateCharacterInput, 'name' | 'mode' | 'type' | 'attributes' | 'style' | 'description'>,
    ) => api.post<{ prompt: string }>('/character-creator/preview-prompt', input),
  });
}

export function useLipsyncComposite(characterId: string) {
  return useMutation({
    mutationFn: (input: {
      text: string;
      backgroundAssetId: string;
      position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'center';
      scale?: number;
      emotion?: string;
      voiceOverride?: string;
    }) =>
      api.post<LipsyncCompositeResult>(
        `/character-creator/${characterId}/lipsync-composite`,
        input,
      ),
  });
}

/**
 * Polea el status de un LipsyncJob async cada 3s mientras esté pending.
 * Cuando llega a completed/failed, se detiene el polling.
 */
export function useLipsyncJobStatus(jobId: string | null) {
  return useQuery({
    queryKey: ['lipsync-job', jobId],
    queryFn: () => api.get<JobStatus>(`/character-creator/lines/jobs/${jobId}`),
    enabled: !!jobId && jobId !== 'cached' && jobId !== 'sync-audio',
    refetchInterval: (q) => {
      const data = q.state.data as JobStatus | undefined;
      if (!data || data.status === 'pending') return 3000;
      return false;
    },
  });
}

// ── Serie de escenas ─────────────────────────────────────────────────────────
// Wizard de 6 pasos con 3 puertas de aprobación. Ver
// docs/interfaz/SECCION_SERIE_DE_ESCENAS.md

export interface SceneSeriesScene {
  id: string;
  title: string;
  description: string;
  durationSec: number;
  keyframePrompt?: string;
  /** Referencias de ESTA toma (nunca una bolsa común de la serie). */
  referenceAssetIds?: string[];
  keyframeAssetId?: string | null;
  keyframeApproved: boolean;
  videoPrompt?: string;
  videoAssetId?: string | null;
  videoModel?: string | null;
  videoApproved: boolean;
  narrationText?: string;
  narrationAssetId?: string | null;
  /** Si la toma vino de un video ya hecho que se está corrigiendo. */
  sourceVideoAssetId?: string | null;
  errorMessage?: string | null;
}

export interface SceneSeriesCostLine {
  step: string;
  label: string;
  usd: number | null;
  detail: string;
}

export interface SceneSeriesState {
  job: {
    id: string;
    title: string;
    mode: string;
    style: string;
    pacing: string;
    aspectRatio: string;
    step: string;
    status: string;
    narrationEnabled: boolean;
    musicAssetId: string | null;
    draftModel: string;
    finalModel: string;
    rhythmVideoAssetId: string | null;
    finalVideoAssetId: string | null;
    joinMode: string;
    crossfadeSec: number;
    costUsd: string | null;
  };
  scenes: SceneSeriesScene[];
  estimate: {
    lines: SceneSeriesCostLine[];
    totalUsd: number;
    partial: boolean;
    naiveUsd: number | null;
    savingsUsd: number | null;
  };
  reachableSteps: string[];
}

export function useSceneSeries(id: string | null) {
  return useQuery({
    queryKey: ['scene-series', id],
    queryFn: () => api.get<SceneSeriesState>(`/scene-series/${id}`),
    enabled: !!id,
  });
}

export function useCreateSceneSeries() {
  return useMutation({
    mutationFn: (input: {
      title: string;
      projectId?: string;
      instanceId?: string;
      mode?: string;
      style?: string;
      pacing?: string;
      aspectRatio?: string;
      scenes: Array<{
        title: string;
        description: string;
        durationSec: number;
        keyframePrompt?: string;
        referenceAssetIds?: string[];
      }>;
    }) => api.post<{ id: string }>('/scene-series', input),
  });
}

/** Todas las acciones del wizard invalidan el estado de la serie. */
function useSeriesMutation<TInput>(
  id: string | null,
  fn: (input: TInput) => Promise<unknown>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scene-series', id] });
    },
  });
}

/** Llena una serie vacía con videos ya hechos, para corregirlos. */
export function useSeedFromVideos(id: string | null) {
  return useSeriesMutation(id, (videoAssetIds: string[]) =>
    api.post<{ scenes: number }>(`/scene-series/${id}/seed-from-videos`, { videoAssetIds }),
  );
}

export function useUpdateSceneSeriesScenes(id: string | null) {
  return useSeriesMutation(
    id,
    (scenes: Array<{
      id: string;
      title?: string;
      description?: string;
      durationSec?: number;
      keyframePrompt?: string;
      referenceAssetIds?: string[];
    }>) =>
    api.patch(`/scene-series/${id}/scenes`, { scenes }),
  );
}

export function useAddSceneSeriesScene(id: string | null) {
  return useSeriesMutation(id, (scene: { title: string; description: string; durationSec?: number }) =>
    api.post(`/scene-series/${id}/scenes`, scene),
  );
}

export function useRemoveSceneSeriesScene(id: string | null) {
  return useSeriesMutation(id, (sceneId: string) => api.del(`/scene-series/${id}/scenes/${sceneId}`));
}

export function useSceneSeriesLook(id: string | null) {
  return useSeriesMutation(id, (look: { style?: string; pacing?: string; draftModel?: string; finalModel?: string }) =>
    api.patch(`/scene-series/${id}/look`, look),
  );
}

export function useGenerateKeyframe(id: string | null) {
  return useSeriesMutation(
    id,
    (input: { sceneId: string; prompt?: string; referenceAssetIds?: string[]; quality?: 'draft' | 'final' }) =>
      api.post<{ assetId: string; url: string; costUsd: number }>(
        `/scene-series/${id}/scenes/${input.sceneId}/keyframe`,
        { prompt: input.prompt, referenceAssetIds: input.referenceAssetIds, quality: input.quality },
      ),
  );
}

export function useApproveKeyframe(id: string | null) {
  return useSeriesMutation(id, (input: { sceneId: string; approved: boolean }) =>
    api.post(`/scene-series/${id}/scenes/${input.sceneId}/keyframe/approve`, { approved: input.approved }),
  );
}

export function useSceneSeriesAudio(id: string | null) {
  return useSeriesMutation(id, (input: {
    narrationEnabled?: boolean;
    narrationVoiceId?: string | null;
    musicAssetId?: string | null;
    musicGainDb?: number;
    narrationByScene?: Array<{ id: string; narrationText: string }>;
  }) => api.patch(`/scene-series/${id}/audio`, input));
}

export function useBuildRhythm(id: string | null) {
  return useSeriesMutation(id, () =>
    api.post<{ assetId: string; url: string; durationSec: number }>(`/scene-series/${id}/rhythm`, {}),
  );
}

export function useGenerateShot(id: string | null) {
  return useSeriesMutation(id, (input: { sceneId: string; quality?: 'draft' | 'final'; prompt?: string }) =>
    api.post<{ assetId: string; url: string; costUsd: number; model: string }>(
      `/scene-series/${id}/scenes/${input.sceneId}/shot`,
      { quality: input.quality, prompt: input.prompt },
    ),
  );
}

export function useApproveShot(id: string | null) {
  return useSeriesMutation(id, (input: { sceneId: string; approved: boolean }) =>
    api.post(`/scene-series/${id}/scenes/${input.sceneId}/shot/approve`, { approved: input.approved }),
  );
}

export function useBuildFinal(id: string | null) {
  return useSeriesMutation(id, () =>
    api.post<{ assetId: string; url: string; durationSec: number }>(`/scene-series/${id}/final`, {}),
  );
}

export function useSetSceneSeriesStep(id: string | null) {
  return useSeriesMutation(id, (step: string) => api.patch(`/scene-series/${id}/step`, { step }));
}
